import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';

import type {
  UpstreamApiDashboardData,
  WidgetConfig,
} from 'src/types/dashboard';
import type {
  Aggregate,
  AggregateList,
} from 'src/components/followUp/common/types';
import { getWidget } from 'src/components/survey/SurveySelectionPage/components/SurveyWidgetView/WidgetTile/widgetMaps';

import WidgetPlaceholder from './WidgetPlaceholder';

import { styles } from './WidgetWrapper.styles';

interface SurveyResultWidgetWrapperProps {
  data?: UpstreamApiDashboardData | Aggregate | AggregateList;
  errorMsg?: string;
  widgetConfig?: WidgetConfig | null;
  comparisonData?: UpstreamApiDashboardData | Aggregate | AggregateList;
}

const WidgetWrapper: FC<SurveyResultWidgetWrapperProps> = ({
  data,
  errorMsg,
  widgetConfig,
  comparisonData,
}) => {
  const { t } = useTranslation('survey');

  const Widget = useMemo(
    () => getWidget(widgetConfig?.widget_type),
    [widgetConfig?.widget_type],
  );

  if (errorMsg) {
    return (
      <Stack direction="column" sx={styles.wrapper}>
        <WidgetPlaceholder errorMsg={errorMsg} />
      </Stack>
    );
  }

  return (
    <Stack direction="column" sx={styles.wrapper}>
      <Box sx={styles.widget}>
        {!widgetConfig || !data || !Widget ? (
          <Stack
            direction="column"
            sx={{
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography
              sx={{
                textAlign: 'center',
              }}
            >
              {t('widget.noPreview')}
            </Typography>
          </Stack>
        ) : (
          <Widget
            data={data as UpstreamApiDashboardData & Aggregate & AggregateList}
            widgetConfig={widgetConfig}
            comparisonData={
              comparisonData as
                | (UpstreamApiDashboardData & Aggregate)
                | undefined
            }
          />
        )}
      </Box>
    </Stack>
  );
};

export default WidgetWrapper;
